import { AdsRewardUserResBody } from '@tmap-web-lib/remote-api-client/src/frontman/apis/common/advertisement/types.ts';
import { TmapApp } from '@tmap-web-lib/tmap-app-interface';
import { TmapLog } from '@tmap-web-lib/tmap-log-client';
import { isTmapScheme } from '@tmap-web-lib/utils';
import { useAtomValue } from 'jotai';
import { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { gte } from 'semver';
import UAParser from 'ua-parser-js';

import { makeDialogPopup } from '../../components/DialogPopup';
import { FIREBASE_SERVICE_PAGE_ID_NAME, VERTICAL_BRIDGE_SERVICES } from '../../constants';
import { APP_ENV } from '../../constants/app-env.ts';
import { IOS_PORTRAIT_ONLY_VERSION } from '../../constants/version.ts';
import { useTmapLog } from '../../hooks';
import useServicePageIdMapping, { Mappings } from '../../hooks/useServicePageIdMapping';
import { usePageIdContext } from '../../providers/page-id-context';
import { useAdsRewardUser } from '../../react-query';
import { tmapInfoAtom } from '../../store/initialState.ts';
import { parseSchemeToOpenServiceByNameParams } from '../../utils/scheme.ts';
import { getFirebaseTargetEnv } from '../../utils/utils';

interface Props extends PropsWithChildren<{}> {}

const ua = new UAParser(window.navigator.userAgent).getResult();

function CheckPageId(props: Props) {
  const tmapInfo = useAtomValue(tmapInfoAtom);
  const { children } = props;
  const pageIdContext = usePageIdContext();
  const { mappings, error, loading } = useServicePageIdMapping();
  const [needUuid, setNeedUuid] = useState(false);
  const [serviceDataInfo, setServiceDataInfo] = useState<Mappings | null>(null);
  // minVersion, maxVersion 값이 있는 경우만 mixpanel 로그 남기도록
  const [hasVersionTarget, setHasVersionTarget] = useState(false);
  const [isTmapLogInitialized, setIsTmapLogInitialized] = useState(false);
  const { data: userUuid, isError: isUuidError } = useAdsRewardUser({ enabled: needUuid });
  const isReady = mappings && !error && !loading;
  const { eventLog, exposeLog } = useTmapLog();
  // 훗날 호출할 "대기 중 리다이렉트" 콜백을 저장
  const pendingRedirectRef = useRef<null | ((uuid: AdsRewardUserResBody) => void)>(null);

  const isPrd = APP_ENV.TARGET === 'prd';

  const hasRedirectedRef = useRef(false);
  const eventIfReadyRef = useRef<
    (...args: Parameters<typeof eventLog>) => Promise<void> | undefined
  >(async () => {});
  const exposeIfReadyRef = useRef<
    (...args: Parameters<typeof exposeLog>) => Promise<void> | undefined
  >(async () => {});

  useEffect(() => {
    eventIfReadyRef.current = async (...args) => {
      if (!isTmapLogInitialized) return;
      return eventLog(...args);
    };
    exposeIfReadyRef.current = async (...args) => {
      if (!isTmapLogInitialized) return;
      return exposeLog(...args);
    };
  }, [isTmapLogInitialized, eventLog, exposeLog]);

  useEffect(() => {
    if (!needUuid || !pendingRedirectRef.current) return;
    if (!isTmapLogInitialized) return;
    if (!userUuid && !isUuidError) return;

    try {
      const userUUidData = userUuid ?? { data: { uuid: '', hasPiAgreed: false, piAgreedDate: '' } };
      pendingRedirectRef.current(userUUidData);
    } finally {
      pendingRedirectRef.current = null;
      setNeedUuid(false);
    }
  }, [needUuid, userUuid, isUuidError, isTmapLogInitialized]);

  useEffect(() => {
    if (serviceDataInfo) {
      const { service = '' } = pageIdContext;
      const serviceData = serviceDataInfo.find((serviceMap) => serviceMap.service === service);

      const targetMinVersion =
        TmapApp.env.isIOS && serviceData?.iosMinVersion
          ? serviceData?.iosMinVersion
          : serviceData?.minVersion;
      const targetMaxVersion =
        TmapApp.env.isIOS && serviceData?.iosMaxVersion
          ? serviceData?.iosMaxVersion
          : serviceData?.maxVersion;

      if (targetMinVersion || targetMaxVersion) {
        setHasVersionTarget(true);
      } else {
        setHasVersionTarget(false);
      }
    }
  }, [serviceDataInfo, pageIdContext]);

  useEffect(() => {
    if (hasVersionTarget || needUuid) {
      TmapLog.init({
        serviceId: 'vertical_bridge',
        appVersion: tmapInfo.appVersion,
        carrier: tmapInfo.carrierName,
        deviceId: tmapInfo.deviceid,
        sessionId: tmapInfo.sessionid,
        mixpanel: {
          api_host: APP_ENV.LOG_COLLECTOR_URL,
          api_payload_format: 'base64',
          api_method: 'POST',
          token: APP_ENV.LOG_COLLECTOR_TOKEN,
          identity: tmapInfo.euk,
          debug: !isPrd,
        },
        onSend: (tmapLogBuilder) => {
          tmapLogBuilder
            .setPageType('WEB')
            .setCustom('deploy_env', APP_ENV.TARGET)
            .setEuk(tmapInfo.euk);
        },
      });
      setIsTmapLogInitialized(true);
    }
  }, [
    hasVersionTarget,
    needUuid,
    isPrd,
    tmapInfo.appVersion,
    tmapInfo.carrierName,
    tmapInfo.deviceid,
    tmapInfo.euk,
    tmapInfo.sessionid,
  ]);

  const redirectTargetService = useCallback(
    async (serviceMappings: Mappings) => {
      const { extraObject, pageid = '', service = '', searchParams } = pageIdContext;
      console.log('searchParams', searchParams);

      if (!service) {
        throw new Error('service가 없음');
      }

      const serviceData = serviceMappings.find((serviceMap) => serviceMap.service === service);

      if (!serviceData) {
        throw new Error('serviceData가 존재하지 않습니다.');
      }

      const {
        url: originUrl,
        requestData,
        iosUrl,
        minVersion,
        maxVersion,
        iosMinVersion,
        iosMaxVersion,
        altUrl,
      } = serviceData;
      let url = originUrl;
      const targetMinVersion = TmapApp.env.isIOS && iosMinVersion ? iosMinVersion : minVersion;
      const targetMaxVersion = TmapApp.env.isIOS && iosMaxVersion ? iosMaxVersion : maxVersion;

      if (targetMinVersion && !gte(TmapApp.env.appVersion, targetMinVersion)) {
        const logbox = extraObject.logbox;
        await eventIfReadyRef.current(`/${service}`, 'popup.update', logbox, 300);

        makeDialogPopup({
          onCancelClick: async () => {
            await eventIfReadyRef.current(`/${service}`, 'popup.update_close', logbox, 300);
            TmapApp.onBackKeyPressed();
          },
          onConfirmClick: async () => {
            await eventIfReadyRef.current(`/${service}`, 'popup.update_confirm', logbox, 300);
            const url = TmapApp.utils.getTmapAppDownloadUrl();
            TmapApp.openBrowser({ url });
            TmapApp.onBackKeyPressed();
          },
        });

        return;
      }

      if (targetMaxVersion && !gte(targetMaxVersion, TmapApp.env.appVersion)) {
        const logbox = extraObject.logbox;
        await eventIfReadyRef.current(`/${service}`, 'redirect.altUrl', logbox);
        if (!altUrl) throw new Error('maxVersion이 존재하지만 altUrl이 존재하지 않습니다.');
        url = altUrl;
      }

      if (!url) {
        throw new Error('serviceUrl이 존재하지 않습니다.');
      }

      const { service: _, pageid: __, extra: ___, ...restParams } = searchParams || {};
      const restParamString = Object.entries(restParams)
        .reduce((searchParams, [key, value]) => {
          let encodedValue = '';
          if (typeof value === 'object' && value != null) {
            encodedValue = encodeURIComponent(JSON.stringify(value));
          } else {
            encodedValue = value || '';
          }
          searchParams.append(key, encodedValue);
          return searchParams;
        }, new URLSearchParams())
        .toString();

      const urlObj = new URL(url);
      const hasPortraitOnly = urlObj.searchParams.has('portraitonly');
      const useStatusBarArea = urlObj.searchParams.get('useStatusBarArea') === 'true';
      const hasQuery = url.includes('?');
      const separator = hasQuery ? '&' : '?';

      const queryParams = new URLSearchParams();
      queryParams.set('extra', JSON.stringify(extraObject));
      if (restParamString) {
        const extraParams = new URLSearchParams(restParamString);
        extraParams.forEach((value, key) => {
          queryParams.set(key, value);
        });
      }

      async function finalRedirect(
        urlWithUuid?: string,
        uuidValue?: string,
        uuidResult?: 'success' | 'fail'
      ) {
        const fullUrl =
          urlWithUuid && uuidValue
            ? urlWithUuid.replace('{tmap.uuid}', uuidValue)
            : `${url}${separator}${queryParams.toString()}${pageid ? `&pageid=${pageid}` : ''}`;
        console.log('fullUrl', fullUrl);

        if (uuidResult) {
          await exposeIfReadyRef.current('/redirect', undefined, {
            uuid: uuidResult,
            redirection_url: fullUrl,
          });
        }

        /**
         * @link - https://tmobi.atlassian.net/browse/TMAPWEB-5261
         * tmap:// 앱스킴이 url로 설정된 경우 현재창을 닫고 openServiceByName으로 서비스를 엽니다.
         * tmap://{serviceName} 형식에서 serviceName을 추출하여 호출합니다.
         */
        if (isTmapScheme(fullUrl)) {
          const params = parseSchemeToOpenServiceByNameParams(url);
          TmapApp.openServiceByName(params);

          setTimeout(() => {
            TmapApp.onBackKeyPressed();
          }, 500);

          return;
        }

        /**
         * @link - https://tmobi.atlassian.net/browse/TMAPWEB-4497
         * @link - https://tmobi.atlassian.net/browse/TMAPCLIENT-26638
         * [IOS] 10.16.0 미만에서 배경에 세로 고정 웹뷰 있을 시, openServiceByUrl 및 onBackKeyPressed 하면 openServiceByUrl로 오픈한 웹뷰가 닫히는 버그 대응
         */
        if (
          hasPortraitOnly &&
          TmapApp.env.isIOS &&
          gte(TmapApp.env.appVersion, IOS_PORTRAIT_ONLY_VERSION)
        ) {
          TmapApp.openServiceByUrl({
            url: fullUrl,
            title: '',
            cacheControl: true,
            portraitonly: true,
            isAnimated: false,
            useStatusBarArea,
          });

          setTimeout(() => {
            TmapApp.onBackKeyPressed();
          }, 500);
        } else {
          if (useStatusBarArea) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            TmapApp.useStatusBarArea({ use: useStatusBarArea });
          }
          window.location.replace(fullUrl);
        }
      }

      function finalRedirectWithUuid(uuidRes: AdsRewardUserResBody) {
        const uuid = uuidRes?.data?.uuid;
        const isIOS = (ua?.os?.name ?? '') === 'iOS';
        const targetUrl = isIOS && iosUrl ? iosUrl : serviceData?.url;

        if (uuid && targetUrl) {
          finalRedirect(targetUrl, uuid, 'success');
        } else {
          finalRedirect(undefined, undefined, 'fail');
        }
      }

      if (requestData && Array.isArray(requestData) && requestData.length > 0) {
        let uuidRequested = false;

        requestData.forEach((key) => {
          if (key === 'uuid') {
            uuidRequested = true;
          } else if (tmapInfo[key]) {
            queryParams.set(key, tmapInfo[key]);
          }
        });

        if (uuidRequested) {
          // uuid가 필요하면: 나중에 userUuid가 준비되었을 때 사용할 콜백 저장
          pendingRedirectRef.current = finalRedirectWithUuid;
          setNeedUuid(true);
        } else {
          // uuid가 필요 없으면 즉시 리다이렉트
          pendingRedirectRef.current = null;
          finalRedirect();
        }
      } else {
        // requestData가 없으면 바로 리다이렉트
        pendingRedirectRef.current = null;
        finalRedirect();
      }
    },
    [pageIdContext, tmapInfo]
  );

  // 에러가 발생했을 때 처리
  const resolveError = useCallback(() => {
    TmapApp.getUserSetting({ key: 'local.server_type' }).then((serverType: string) => {
      const currentEnv = import.meta.env.VITE_PUBLIC_TARGET;
      let targetEnv = getFirebaseTargetEnv(currentEnv);

      if (serverType === 'rtg') {
        if (currentEnv === 'prd' || currentEnv === 'rtg') {
          targetEnv = 'rtg';
        } else {
          targetEnv = 'dtg';
        }
      }

      if (targetEnv) {
        setServiceDataInfo(
          VERTICAL_BRIDGE_SERVICES[
            `${FIREBASE_SERVICE_PAGE_ID_NAME}_${getFirebaseTargetEnv(currentEnv)}`
          ]
        );
        redirectTargetService(
          VERTICAL_BRIDGE_SERVICES[
            `${FIREBASE_SERVICE_PAGE_ID_NAME}_${getFirebaseTargetEnv(currentEnv)}`
          ]
        );
      } else {
        setServiceDataInfo(VERTICAL_BRIDGE_SERVICES[FIREBASE_SERVICE_PAGE_ID_NAME as string]);
        redirectTargetService(VERTICAL_BRIDGE_SERVICES[FIREBASE_SERVICE_PAGE_ID_NAME as string]);
      }
    });
  }, [redirectTargetService]);

  useEffect(() => {
    if (!isReady || hasRedirectedRef.current) {
      return;
    }

    hasRedirectedRef.current = true;

    try {
      setServiceDataInfo(mappings as Mappings);
      redirectTargetService(mappings as Mappings);
    } catch (e) {
      resolveError();
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, mappings]);

  return <>{children}</>;
}

export { CheckPageId };
